import React, { useEffect, useState } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import Header from './components/Header';
import LegacyActivityRedirect from './components/LegacyActivityRedirect';
import LegacyDestinationRedirect from './components/LegacyDestinationRedirect';
import Footer from './components/Footer';
import Seo from './components/Seo';
import JsonLd from './components/JsonLd';
import CookieConsent from './components/CookieConsent';
import GlobalSeoConfig from './components/GlobalSeoConfig';
import { trackPageView } from './lib/analytics';
import Home from './pages/Home';
import Activities from './pages/Activities';
import ActivityDetail from './pages/ActivityDetail';
import CategoryLanding from './pages/CategoryLanding';
import Destinations from './pages/Destinations';
import Destination from './pages/Destination';
import Blog from './pages/Blog';
import BlogDetail from './pages/BlogDetail';
import About from './pages/About';
import Contact from './pages/Contact';
import Favorites from './pages/Favorites';
import PaymentResult from './pages/PaymentResult';
import FloatingWhatsApp from './components/FloatingWhatsApp';
import { Toaster } from './components/ui/toaster';
import { CurrencyProvider } from './context/CurrencyContext';
import { WishlistProvider } from './context/WishlistContext';
import { LanguageProvider } from './i18n/LanguageContext';
import NotFound from './pages/NotFound';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Login from './pages/Login';
import Register from './pages/Register';
import MyAccount from './pages/MyAccount';
import AdminLayout from './components/AdminLayout';
import AdminMedia from './pages/AdminMedia';
import AdminActivities from './pages/AdminActivities';
import AdminCoupons from './pages/AdminCoupons';
import AdminDashboard from './pages/AdminDashboard';
import AdminNewsletter from './pages/AdminNewsletter';
import AdminReservations from './pages/AdminReservations';
import AdminCustomers from './pages/AdminCustomers';
import AdminBlog from './pages/AdminBlog';
import AdminDestinations from './pages/AdminDestinations';
import AdminSettings from './pages/AdminSettings';
import AdminAccount from './pages/AdminAccount';
import { getRouteSeo, buildLangAlternates, canonicalForLang, isNoindexPath, organizationJsonLd, websiteJsonLd } from './lib/seo';
import { useLanguage } from './i18n/LanguageContext';

const THEME_STORAGE_KEY = 'owa-theme';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

function RouteMetadata() {
  const location = useLocation();
  const { lang, currentLanguage, languages } = useLanguage();
  const path = location.pathname;
  const meta = getRouteSeo(path);
  const noindex = isNoindexPath(path);

  // Private/auth pages: no canonical juggling, no hreflang, keep them out of the
  // index. Public pages get lang-aware canonical + hreflang alternates.
  const seoProps = noindex
    ? { ...meta, robots: 'noindex,nofollow' }
    : {
        ...meta,
        canonical: canonicalForLang(path, lang),
        alternates: buildLangAlternates(path),
        ogLocale: currentLanguage.locale.replace('-', '_'),
        ogLocaleAlternates: languages
          .filter((l) => l.code !== lang)
          .map((l) => l.locale.replace('-', '_')),
      };

  return (
    <>
      <Seo {...seoProps} />
      {/* Site-wide structured data (helps Google understand the brand + search box). */}
      <JsonLd data={organizationJsonLd()} />
      <JsonLd data={websiteJsonLd()} />
    </>
  );
}

function getInitialTheme() {
  if (typeof window === 'undefined') {
    return 'light';
  }

  const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (storedTheme === 'light' || storedTheme === 'dark') {
    return storedTheme;
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function App() {
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    const root = document.documentElement;
    const isDark = theme === 'dark';

    root.classList.toggle('dark', isDark);
    root.dataset.theme = theme;
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  return (
    <div className="App">
      <CurrencyProvider>
      <WishlistProvider>
      <LanguageProvider>
          <BrowserRouter>
            <ScrollToTop />
            <RouteMetadata />
            <GlobalSeoConfig />
            <AppShell theme={theme} onToggleTheme={() => setTheme((currentTheme) => currentTheme === 'dark' ? 'light' : 'dark')} />
          </BrowserRouter>
      </LanguageProvider>
      </WishlistProvider>
    </CurrencyProvider>
    </div>
  );
}

// Renders the public chrome (Header/Footer/WhatsApp) around the routed content,
// but hides it on the /admin panel, which brings its own WordPress-style frame.
function AppShell({ theme, onToggleTheme }) {
  const { pathname } = useLocation();
  const { lang } = useLanguage();
  const isAdmin = pathname === '/admin' || pathname.startsWith('/admin/');

  // GA4 page_view on every route change (Consent Mode gates the actual data).
  useEffect(() => {
    trackPageView({ path: pathname, lang });
  }, [pathname, lang]);

  return (
    <>
      {!isAdmin ? <Header theme={theme} onToggleTheme={onToggleTheme} /> : null}
      <main>
        <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/activities" element={<Activities />} />
                <Route path="/activities/:category" element={<CategoryLanding />} />
                <Route path="/activity/:id" element={<ActivityDetail />} />
                <Route path="/destinations" element={<Destinations />} />
                <Route path="/destinations/:slug" element={<Destination />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:id" element={<BlogDetail />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/favorites" element={<Favorites />} />
                <Route path="/account" element={<MyAccount />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                {/* SumUp payment return — /pago/resultado is the live redirect_url in the backend; keep it working */}
                <Route path="/pago/resultado" element={<PaymentResult />} />
                <Route path="/payment/result" element={<PaymentResult />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<Navigate to="/admin/dashboard" replace />} />
                  <Route path="dashboard" element={<AdminDashboard />} />
                  <Route path="activities" element={<AdminActivities />} />
                  <Route path="reservations" element={<AdminReservations />} />
                  <Route path="destinations" element={<AdminDestinations />} />
                  <Route path="blog" element={<AdminBlog />} />
                  <Route path="customers" element={<AdminCustomers />} />
                  <Route path="coupons" element={<AdminCoupons />} />
                  <Route path="newsletter" element={<AdminNewsletter />} />
                  <Route path="media" element={<AdminMedia />} />
                  <Route path="settings" element={<AdminSettings />} />
                  <Route path="account" element={<AdminAccount />} />
                </Route>

                {/* Legacy Spanish routes → permanent redirects so previously shared links keep working */}
                <Route path="/actividades" element={<Navigate to="/activities" replace />} />
                <Route path="/actividad/:id" element={<LegacyActivityRedirect />} />
                <Route path="/destinos/:slug" element={<LegacyDestinationRedirect />} />
                <Route path="/destinos" element={<Navigate to="/destinations" replace />} />
                <Route path="/nosotros" element={<Navigate to="/about" replace />} />
                <Route path="/contacto" element={<Navigate to="/contact" replace />} />
                <Route path="/favoritos" element={<Navigate to="/favorites" replace />} />
                <Route path="/mi-cuenta" element={<Navigate to="/account" replace />} />
                <Route path="/acceso" element={<Navigate to="/login" replace />} />
                <Route path="/registro" element={<Navigate to="/register" replace />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
            {!isAdmin ? <Footer /> : null}
            {!isAdmin ? <FloatingWhatsApp /> : null}
            <CookieConsent />
            <Toaster />
    </>
  );
}

export default App;
